import React, { useState, useEffect, useRef } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faSearch,
  faFilterList,
  faUserMinus,
} from "@fortawesome/pro-regular-svg-icons";
import useExporter from "../../hooks/useExporter";
import PriceFormatter from "../../helpers/PriceFormater";
import { Link, useParams } from "react-router-dom";
import HeaderBar from "../../components/HeaderBar";
import Sidebar from "../../components/Sidebar";
import Modal from "../../components/Modal";
import Dropdown from "../../components/Dropdown";
import {
  getUsersByTenant,
  deleteUser,
  updateBillingGroupAndAccountTypeOnUser,
  getScoFormSettings,
} from "../../api/Users";
import { useAuth } from "../../providers/AuthContext";
import InputField from "../../components/InputField";
import toast from "react-hot-toast";
import ConfirmationModal from "../../components/ConfirmationModal";
import LoadingTablePage from "../LoadingTablePage";
import Breadcrumbs from "../../components/Breadcrumbs";
import { getBillingGroupsByAgreement } from "../../api/BillingGroups";
import { getAccountTypesByAgreement } from "../../api/Agreement";
import TableView from "../../components/TableView";
import MultipleSelectDropdown from "../../components/MultipleSelectDropdown";
import Switch from "../../components/Switch";

const UsersPage = () => {
  const { tenantPublicId, agreementPublicId } = useParams();
  const { getAccessToken } = useAuth();
  const { handleExport } = useExporter();

  const [users, setUsers] = useState([]);

  const [sortColumn, setSortColumn] = useState("");
  const [sortOrder, setSortOrder] = useState("asc");
  const [search, setSearch] = useState("");
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [filteredUsers, setFilteredUsers] = useState([]);

  const [bulkActionAccountType, setBulkActionAccountType] = useState(false);
  const [bulkActionBillingGroup, setBulkActionBillingGroup] = useState(false);

  const [billingGroups, setBillingGroups] = useState([]);
  const [accountTypes, setAccountTypes] = useState([]);
  const [selectedBillingGroup, setSelectedBillingGroup] = useState(null);
  const [selectedAccountType, setSelectedAccountType] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersLoaded, setUsersLoaded] = useState(false);
  const [supportsOffboarding, setSupportsOffboarding] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [confirmState, setConfirmState] = useState({
    isOpen: false,
    title: "",
    confirm: () => {},
  });
  const modalRef = useRef();

  const handleOpenModal = () => {
    if (selectedUsers.length === 0) {
      toast.error("No users selected");
      return;
    }
    setIsModalOpen(true);
  };

  const handleExportToExcel = () => {
    const usersToExport =
      selectedUsers.length > 0
        ? filteredUsers.filter((user) =>
            selectedUsers.includes(user.userPublicId)
          )
        : filteredUsers;

    if (usersToExport.length === 0) {
      toast.error("No users selected");
      return;
    }

    const usersData = usersToExport.map(
      ({
        userPublicId,
        tenantPublicId,
        displayName,
        role,
        userPrincipalName,
        officeLocation,
        country,
        mobilePhone,
        jobTitle,
        managerDisplayName,
        lastSignInDateTime,
        OnPremisesSyncEnabled,
        accountTypeName,
        accountTypePrice,
        accountTypeStatus,
        billingGroupName,
        billingGroupStatus,
        createdBy,
        createdDateTimeUTC,
        updatedBy,
        updatedDateTimeUTC,
      }) => ({
        "User Id": userPublicId,
        "Tenanant Id": tenantPublicId,
        Name: displayName,
        Role: role,
        Username: userPrincipalName,
        "Office location": officeLocation,
        Country: country,
        Phone: mobilePhone,
        "Job title": jobTitle,
        Manager: managerDisplayName,
        "Last login": lastSignInDateTime,
        "On-prem sync": OnPremisesSyncEnabled,
        "Accout name": accountTypeName,
        "Account type price": accountTypePrice,
        "Account status": accountTypeStatus,
        "Billing group name": billingGroupName,
        "Billing group status": billingGroupStatus,
        "Createdb by": createdBy,
        "Created date": createdDateTimeUTC,
        "Updated by": updatedBy,
        "Updated date": updatedDateTimeUTC,
      })
    );

    const currentDate = new Date().toISOString().split("T")[0];
    const filename = `Users_${currentDate}`;

    handleExport({ Users: usersData }, filename);
  };

  const handleChangeBillingGroup = async () => {
    if (selectedUsers.length === 0) {
      toast.error("Please select users");
      return;
    }

    try {
      const accessToken = await getAccessToken();

      await updateBillingGroupAndAccountTypeOnUser(
        {
          billingGroupPublicId: bulkActionBillingGroup
            ? selectedBillingGroup
            : null,
          accountTypePublicId: bulkActionAccountType
            ? selectedAccountType
            : null,
          userPublicIds: selectedUsers,
          updatedDateTimeUTC: new Date().toISOString(),
        },
        accessToken
      );

      toast.success(
        "Billing group / account type updated successfully for selected users!",
        {
          iconTheme: {
            primary: "#4CAF50",
            secondary: "#DFF2BF",
          },
        }
      );

      handleGetUsers();
      setSelectedUsers([]);
      setIsModalOpen(false);
    } catch (error) {
      console.error("Failed to update billing group:", error);
      toast.error("Failed to update billing group for selected users.");
    }
  };

  const handleSelectBillingGroup = (billingGroupName) => {
    setSelectedBillingGroup(
      billingGroups.find((group) => group.name === billingGroupName)
        ?.billingGroupPublicId || null
    );
  };

  const handleSelectAccountType = (accountType) => {
    setSelectedAccountType(
      accountTypes.find((at) => at.name === accountType)?.accountTypePublicId ||
        []
    );
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (modalRef.current && !modalRef.current.contains(event.target)) {
        setIsFilterOpen(false);
      }
    };
    if (isFilterOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isFilterOpen]);

  useEffect(() => {
    handleGetUsers();

    const handleUserUpdate = () => {
      handleGetUsers();
    };
    window.addEventListener("userUpdated", handleUserUpdate);

    return () => {
      window.removeEventListener("userUpdated", handleUserUpdate);
    };
  }, [tenantPublicId]);

  const [filters, setFilters] = useState({
    billingGroup: [],
    accountType: [],
    role: [],
    lastSignInDateTimeUtc: "",
    licenses: [],
    onPremSyncEnabled: null,
  });

  const onPremOptions = [
    { label: "All", value: null },
    { label: "Yes", value: true },
    { label: "No", value: false },
  ];

  useEffect(() => {
    const applyFilterAndSort = () => {
      let filtered = users;

      if (search) {
        const searchLower = search.toLowerCase();
        filtered = users.filter((user) => {
          const name = (user.displayName ?? "").toLowerCase();
          const upn = (user.userPrincipalName ?? "").toLowerCase();
          const accountTypeName = (user.accountTypeName ?? "").toLowerCase();
          const billingGroupName = (user.billingGroupName ?? "").toLowerCase();

          return (
            name.includes(searchLower) ||
            upn.includes(searchLower) ||
            accountTypeName.includes(searchLower) ||
            billingGroupName.includes(searchLower)
          );
        });
      }

      if (sortColumn) {
        filtered = [...filtered].sort((a, b) => {
          const aValue = (a[sortColumn] || "").toString().toLowerCase();
          const bValue = (b[sortColumn] || "").toString().toLowerCase();
          return sortOrder === "asc"
            ? aValue.localeCompare(bValue)
            : bValue.localeCompare(aValue);
        });
      }

      if (filters.onPremSyncEnabled !== null) {
        filtered = filtered.filter(
          (user) => user.onPremSyncEnabled === filters.onPremSyncEnabled
        );
      }
      if (filters.accountType.length > 0) {
        filtered = filtered.filter((user) =>
          filters.accountType.includes(user.accountTypeName)
        );
      }
      if (Array.isArray(filters.licenses) && filters.licenses.length > 0) {
        filtered = filtered.filter((user) => {
          const userLicenses = user.licenses
            ? user.licenses
                .split(", ")
                .map((l) => l.trim())
                .filter(Boolean)
            : [];

          return filters.licenses.some((lic) => userLicenses.includes(lic));
        });
      }
      if (filters.billingGroup.length > 0) {
        filtered = filtered.filter((user) =>
          filters.billingGroup.includes(user.billingGroupName)
        );
      }
      if (filters.role.length > 0) {
        filtered = filtered.filter((user) => {
          const userRolesRaw = user.roles
            ? user.roles
                .split(",")
                .map((r) => r.trim())
                .filter(Boolean)
            : [];

          const userRoles = userRolesRaw.map((r) => r.toLowerCase());
          const hasNoRoles = userRolesRaw.length === 0;

          return filters.role.some((role) =>
            role === "No roles"
              ? hasNoRoles
              : userRoles.includes(role.toLowerCase())
          );
        });
      }
      if (filters.lastSignInDateTimeUtc) {
        filtered = filtered.filter((user) => {
          if (!user.lastSignInDateTimeUtc) return false; // Skip if no lastSignInDateTime

          const updated = new Date(user.lastSignInDateTimeUtc);
          const filterDate = new Date(filters.lastSignInDateTimeUtc);
          return updated < filterDate;
        });
      }

      setFilteredUsers(filtered);
    };

    applyFilterAndSort();
  }, [search, users, sortColumn, sortOrder, filters]);

  const handleGetUsers = async () => {
    try {
      const accessToken = await getAccessToken();
      const newUsers = await getUsersByTenant(tenantPublicId, accessToken);
      console.log("API response from getUsersByTenant:", newUsers);

      const formattedUsers = newUsers.map((user) => ({
        ...user,
        accountTypePrice: PriceFormatter(user.accountTypePrice),

        licenses: user.licenses
          ? Object.values(user.licenses)
              .map((l) => l.trim())
              .filter((l) => l && l.length > 0)
              .join(", ")
          : "No licenses",

        roles: Array.isArray(user.roles) ? user.roles.join(", ") : "No roles",
        syncEnabled: user.onPremSyncEnabled ? "Yes" : "No",
      }));

      setUsers(formattedUsers);
      setFilteredUsers(formattedUsers);
      setUsersLoaded(true);
    } catch (error) {
      console.error(error);
      setUsersLoaded(true);
    }
  };

  useEffect(() => {
    const fetchBillingGroups = async () => {
      try {
        const accessToken = await getAccessToken();
        const groups = await getBillingGroupsByAgreement(
          agreementPublicId,
          accessToken
        );
        setBillingGroups(groups);
      } catch (error) {
        console.error("Failed to fetch billing groups:", error);
      }
    };
    fetchBillingGroups();
  }, [agreementPublicId, getAccessToken]);

  const billingGroupOptions = (billingGroups ?? []).map((bg) => ({
    label: bg.name,
    value: bg.name,
  }));

  useEffect(() => {
    const fetchAccountTypes = async () => {
      try {
        const accessToken = await getAccessToken();
        const at = await getAccountTypesByAgreement(
          agreementPublicId,
          accessToken
        );
        setAccountTypes(at);
        console.log("Fetched account types:", at);
      } catch (error) {
        console.error("Failed to fetch account types:", error);
      }
    };

    fetchAccountTypes();
  }, [agreementPublicId, getAccessToken]);

  // The SCO form settings decide whether this customer can offboard from the portal at
  // all; a failure simply leaves the link hidden.
  useEffect(() => {
    const fetchOffboardingSupport = async () => {
      const accessToken = await getAccessToken().catch(() => null);
      if (!accessToken) return;

      const settings = await getScoFormSettings(accessToken).catch(() => null);
      setSupportsOffboarding(!!settings?.supportsOffboarding);
    };

    fetchOffboardingSupport();
  }, [getAccessToken]);

  const accountTypeOptions = (accountTypes ?? []).map((at) => ({
    label: at.name,
    value: at.name,
  }));

  const allLicenses = Array.from(
    new Set([
      ...users.flatMap((u) =>
        typeof u.licenses === "string"
          ? u.licenses
              .split(",")
              .map((l) => l.trim())
              .filter((l) => l && l.length > 0)
          : []
      ),
      "No licenses",
    ])
  );

  const licenseOptions = allLicenses.map((l) => ({ label: l, value: l }));

  const allRoles = Array.from(
    new Set([
      ...users.flatMap((u) =>
        typeof u.roles === "string"
          ? u.roles
              .split(",")
              .map((r) => r.trim())
              .filter((r) => r && r.length > 0)
          : []
      ),
      "No roles",
    ])
  );

  const roleOptions = allRoles.map((r) => ({ label: r, value: r }));

  const handleDeleteUser = async (userPublicId) => {
    const toastId = toast.loading("Deleting user...");
    try {
      const accessToken = await getAccessToken();
      await deleteUser(userPublicId, accessToken);

      const newUsers = await getUsersByTenant(tenantPublicId, accessToken);

      setUsers(newUsers);

      toast.success("User deleted", {
        id: toastId,
        iconTheme: {
          primary: "#4CAF50",
          secondary: "#DFF2BF",
        },
      });
    } catch (error) {
      toast.error("Failed to delete user", { id: toastId });
      console.error(error);
    }
  };

  console.log("Users:", users);

  return (
    <>
      <HeaderBar />
      <div className="flex flex-col">
        <div className="flex flex-row">
          <Sidebar />
          <main className="page">
            <div className="absolute w-full pointer-events-none">
              <LoadingTablePage title={"Users"} loading={!usersLoaded} />
            </div>

            <div
              className={`${
                !usersLoaded ? "loading" : ""
              } page-content pb-[25rem]`}
            >
              <Breadcrumbs />
              <div className="flex justify-between items-center">
                <h2 className="text-2xl text-black font-bold pb-2">Users</h2>

                {/*                 <Link
                  to={`/${agreementPublicId}/${tenantPublicId}/create-user`}
                  className="btn-primary"
                >
                  <FontAwesomeIcon icon={faPlus} className="mr-2 text-lg" />
                  Create user
                </Link> */}

                {/* The link is hidden unless the customer's SCO form supports
                    offboarding — otherwise it would lead to a page that only says the
                    feature is unavailable. */}
                {supportsOffboarding && (
                  <Link
                    to={`/${agreementPublicId}/${tenantPublicId}/offboard-user`}
                    className="btn-primary"
                  >
                    <FontAwesomeIcon icon={faUserMinus} className="mr-2 text-lg" />
                    Offboard user
                  </Link>
                )}
              </div>
              <div className="rounded-md shadow-md border w-full p-4">
                <div className="flex mb-2 gap-4 justify-between">
                  <button
                    className="btn btn-outline-primary-small"
                    onClick={handleOpenModal}
                    disabled={selectedUsers.length === 0}
                  >
                    Edit
                  </button>
                  <div className="flex items-center">
                    <div className="relative">
                      <button
                        className="btn-outline-primary-small mr-4 mt-2 icon"
                        onClick={() => setIsFilterOpen((prev) => !prev)}
                      >
                        <FontAwesomeIcon
                          icon={faFilterList}
                          className="mr-2 text-lg"
                        />
                        Filter
                      </button>
                      {isFilterOpen && (
                        <div className="absolute top-12 z-10" ref={modalRef}>
                          <div className="p-4 bg-white flex gap-4 flex-col w-[22rem] border rounded-xl">
                            <h3>Filter users</h3>

                            <Dropdown
                              label="On-premises sync"
                              options={onPremOptions.map((opt) => opt.label)}
                              value={
                                onPremOptions.find(
                                  (opt) =>
                                    opt.value === filters.onPremSyncEnabled
                                )?.label || "All"
                              }
                              onSelect={(selectedLabel) => {
                                const selectedOption = onPremOptions.find(
                                  (opt) => opt.label === selectedLabel
                                );
                                setFilters((prev) => ({
                                  ...prev,
                                  onPremSyncEnabled: selectedOption.value,
                                }));
                              }}
                            />

                            <MultipleSelectDropdown
                              label="Account type"
                              options={accountTypeOptions}
                              selected={filters.accountType}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  accountType: selected,
                                }))
                              }
                            />
                            <MultipleSelectDropdown
                              label="Billing group"
                              options={billingGroupOptions}
                              selected={filters.billingGroup}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  billingGroup: selected,
                                }))
                              }
                            />

                            <MultipleSelectDropdown
                              label="Licenses"
                              options={licenseOptions}
                              selected={filters.licenses}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  licenses: selected,
                                }))
                              }
                            />

                            <InputField
                              label="Not active since"
                              type="date"
                              value={filters.lastSignInDateTimeUtc}
                              onChange={(e) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  lastSignInDateTimeUtc: e.target.value,
                                }))
                              }
                            />

                            <MultipleSelectDropdown
                              label="User role"
                              options={roleOptions}
                              selected={filters.role}
                              onChange={(selected) =>
                                setFilters((prev) => ({
                                  ...prev,
                                  role: selected,
                                }))
                              }
                            />

                            <button
                              className="btn-outline-primary-small"
                              onClick={() =>
                                setFilters({
                                  accountType: [],
                                  billingGroup: [],
                                  lastSignInDateTimeUtc: "",
                                  role: [],
                                  licenses: [],
                                  onPremSyncEnabled: null,
                                })
                              }
                            >
                              Clear filter
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <InputField
                      iconLeft={false}
                      icon={faSearch}
                      placeholder={"Name, Manager, Email "}
                      value={search}
                      onChange={(v) => {
                        setSearch(v.target.value);
                      }}
                    />
                    <button
                      className="btn-outline-primary-small ml-4 mt-2"
                      onClick={handleExportToExcel}
                    >
                      Export to excel
                    </button>
                  </div>
                </div>

                <div className="overflow-x-auto pb-[250px] mb-[-250px]">
                  <TableView
                    data={filteredUsers}
                    selectedRows={selectedUsers} // Map selectedUsers to selectedRows
                    setSelectedRows={setSelectedUsers} // Ensure selection updates correctly
                    columns={[
                      { key: "displayName", label: "Name" },
                      { key: "roles", label: "Role" },
                      { key: "userPrincipalName", label: "Principal name" },
                      { key: "accountTypeName", label: "Account type" },
                      { key: "accountTypePrice", label: "Account type price" },
                      { key: "licenses", label: "Licenses" },
                      { key: "billingGroupName", label: "Billing group" },
                      { key: "syncEnabled", label: "On prem sync" },
                      { key: "officeLocation", label: "Office location" },
                      { key: "country", label: "Country" },
                      { key: "mobilePhone", label: "Phone" },
                      { key: "jobTitle", label: "Job title" },
                      { key: "managerDisplayName", label: "Manager" },
                      { key: "lastSignInDateTimeUtc", label: "Last login" },
                    ]}
                    primaryKey="userPublicId" // Match this with userPublicId
                    basePath={`/${agreementPublicId}/${tenantPublicId}/users`}
                    handleDelete={handleDeleteUser}
                  />
                </div>
              </div>
            </div>
          </main>
        </div>
      </div>
      <Modal isOpen={isModalOpen} setIsOpen={setIsModalOpen}>
        <div className="bg-white p-6 rounded-lg w-[400px]">
          <h2 className="text-xl font-semibold mb-4">
            Change billing group / account type
          </h2>
          <div className="flex flex-col gap-4">
            <div className="flex items-center gap-4 justify-center">
              <div className="pt-6">
                <Switch
                  checked={bulkActionBillingGroup}
                  onChange={(e) => {
                    setBulkActionBillingGroup(e);
                  }}
                />
              </div>
              <Dropdown
                label="Select billing group"
                options={billingGroups.map((group) => group.name)}
                value={
                  (bulkActionBillingGroup &&
                    billingGroups.find(
                      (group) =>
                        group.billingGroupPublicId === selectedBillingGroup
                    )?.name) ||
                  ""
                }
                disabled={!bulkActionBillingGroup}
                onSelect={handleSelectBillingGroup}
              />
            </div>

            <div className="flex items-center gap-4 justify-center">
              <div className="pt-6">
                <Switch
                  checked={bulkActionAccountType}
                  onChange={(e) => {
                    setBulkActionAccountType(e);
                  }}
                />
              </div>

              <Dropdown
                label="Select account type"
                options={accountTypes.map((at) => at.name)}
                value={
                  (bulkActionAccountType &&
                    accountTypes.find(
                      (at) => at.accountTypePublicId === selectedAccountType
                    )?.name) ||
                  ""
                }
                disabled={!bulkActionAccountType}
                onSelect={handleSelectAccountType}
              />
            </div>
          </div>

          <div className="flex justify-end mt-4 gap-4">
            <button
              className="btn-outline-primary"
              onClick={() => setIsModalOpen(false)}
            >
              Cancel
            </button>
            <button className="btn-primary" onClick={handleChangeBillingGroup}>
              Confirm
            </button>
          </div>
        </div>
      </Modal>

      <ConfirmationModal
        isOpen={confirmState.isOpen}
        title={confirmState.title}
        onClose={() => setConfirmState((s) => ({ ...s, isOpen: false }))}
        onConfirm={() => {
          setConfirmState((s) => ({ ...s, isOpen: false }));
          confirmState.confirm();
        }}
      />
    </>
  );
};

export default UsersPage;
